// SEPARAR EN CAPAS — la herramienta del visor de escritorio
// (ADR-2026-10-07-separar-en-capas-en-el-visor).
//
// El backend parte la creación con Seedream 5.0 Pro Layerize (2K, fast; 1 crédito): una
// base —la imagen sin los elementos— y una capa PNG por elemento, recortada a su caja y con
// nombre. Acá: el pedido, la revisión de la instrucción mientras se escribe (Gemini
// Flash-Lite, gratis, sólo avisa), la geometría de cada capa en el lienzo (mover, escalar,
// rotar, en fracciones de la base) y la composición final, que es lo que se guarda como
// versión nueva (gratis: el crédito ya se cobró al separar).

import { atlasBackendFetch } from './atlasBackend';
import { GenerateAdError } from './generateAdRequest';
import { supabase } from './supabase';

/** Lo que cuesta separar. Espejo de `CREDITOS_DIVISION` del backend. */
export const CREDITOS_SEPARAR_EN_CAPAS = 1;

/** Tope de la instrucción. Espejo de `MAX_LARGO_INSTRUCCION_CAPAS` del backend. */
export const MAX_LARGO_INSTRUCCION_CAPAS = 400;

/** Una capa sobre el lienzo. Posición y tamaño en FRACCIONES de la base (0–1). */
export interface CapaEnLienzo {
  id: string;
  src: string;
  /** Dónde vive el PNG en `generadas` (con el prefijo de la empresa): editar la capa lo usa. */
  path: string;
  nombre: string;
  /** La base (la imagen sin los elementos separados): va siempre al fondo y no se mueve. */
  esBase: boolean;
  visible: boolean;
  /** Centro. */
  cx: number;
  cy: number;
  /** Ancho y alto. */
  w: number;
  h: number;
  /** Grados, en sentido horario. */
  rot: number;
}

export interface Separacion {
  /** De atrás hacia adelante: la base primero. */
  capas: CapaEnLienzo[];
  /** Medidas de la base en píxeles (en las que vinieron las cajas). */
  ancho: number;
  alto: number;
}

interface CapaDelBackend {
  path: string;
  z_index: number;
  nombre?: string;
  caja?: [number, number, number, number];
}

/**
 * Las capas del backend en el lienzo: la base entera y cada capa centrada en su caja. Sin
 * caja (no debería pasar con Seedream), la capa ocupa toda la base, que es lo que hace
 * Qwen.
 */
export function capasEnLienzo(
  capas: ReadonlyArray<CapaDelBackend & { src: string }>,
  ancho: number,
  alto: number,
): CapaEnLienzo[] {
  return [...capas]
    .sort((a, b) => a.z_index - b.z_index)
    .map((c, i) => {
      const esBase = i === 0;
      const [x1, y1, x2, y2] = !esBase && c.caja ? c.caja : [0, 0, ancho, alto];
      return {
        id: `capa-${i}`,
        src: c.src,
        path: c.path,
        nombre: esBase ? 'Fondo' : c.nombre?.trim() || `Capa ${i}`,
        esBase,
        visible: true,
        cx: (x1 + x2) / 2 / ancho,
        cy: (y1 + y2) / 2 / alto,
        w: Math.max(0.001, (x2 - x1) / ancho),
        h: Math.max(0.001, (y2 - y1) / alto),
        rot: 0,
      };
    });
}

/** Las medidas reales de una imagen (para la base, si el backend no las mandó). */
function medirImagen(src: string): Promise<{ ancho: number; alto: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve({ ancho: img.naturalWidth, alto: img.naturalHeight });
    img.onerror = () => reject(new Error('No pudimos cargar las capas. Probá de nuevo.'));
    img.src = src;
  });
}

const MENSAJE_SIN_CREDITOS =
  'No te alcanzan los créditos para separar la imagen. Sumá créditos o actualizá tu plan para continuar.';

/** Separa la creación. Tira `GenerateAdError` de billing en 402, para el modal de saldo. */
export async function separarEnCapas(p: { imagenId: string; instruccion?: string }): Promise<Separacion> {
  const instruccion = p.instruccion?.trim().slice(0, MAX_LARGO_INSTRUCCION_CAPAS);
  const res = await atlasBackendFetch('/v1/images/layers', {
    imagen_id: p.imagenId,
    motor: 'seedream',
    ...(instruccion ? { instruccion } : {}),
    modo_utilizado: 'separar_capas_visor',
  });
  if (res.status === 402) throw new GenerateAdError('billing', MENSAJE_SIN_CREDITOS, 'billing');
  const data = (await res.json().catch(() => null)) as {
    response?: string;
    layer_paths?: string[];
    capas?: CapaDelBackend[];
    base?: { ancho: number; alto: number };
    error?: string;
  } | null;
  if (!res.ok || data?.response !== 'success' || !data.capas?.length) {
    throw new Error(data?.error || 'No pudimos separar la imagen en capas. Probá de nuevo.');
  }
  const paths = data.capas.map((c) => c.path);
  const { data: firmadas } = await supabase.storage.from('generadas').createSignedUrls(paths, 3600);
  // Por path y no por posición: `createSignedUrls` no promete el orden, y de él sale el
  // z-order (el mismo cuidado que `ordenarCapas` del editor Canva).
  const porPath = new Map(firmadas?.map((f) => [f.path ?? '', f.signedUrl ?? null]) ?? []);
  if (paths.some((path) => !porPath.get(path))) {
    throw new Error('Las capas se generaron, pero no pudimos cargarlas. Probá de nuevo.');
  }
  const conSrc = data.capas.map((c) => ({ ...c, src: porPath.get(c.path) as string }));
  const base = data.base ?? (await medirImagen(conSrc[0].src));
  return { capas: capasEnLienzo(conSrc, base.ancho, base.alto), ancho: base.ancho, alto: base.alto };
}

/** El aviso del cuadrito, o `null` si la instrucción tiene sentido (o si falló la revisión). */
export async function revisarInstruccionDeCapas(texto: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await atlasBackendFetch('/v1/images/layers/revisar-instruccion', { instruccion: texto }, { signal });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { tiene_sentido?: boolean; aviso?: string } | null;
    return data?.tiene_sentido === false && data.aviso ? data.aviso : null;
  } catch {
    return null;
  }
}

// ── Geometría ───────────────────────────────────────────────────────────────────

/** Lo mínimo que puede medir una capa (de su lado más largo), en fracción de la base. */
export const LADO_MINIMO = 0.03;

/** Mueve la capa `dx`/`dy` (fracciones de la base). */
export function moverCapa(c: CapaEnLienzo, dx: number, dy: number): CapaEnLienzo {
  return { ...c, cx: c.cx + dx, cy: c.cy + dy };
}

/** Escala la capa sobre su centro, conservando la proporción; nunca más chica que el mínimo. */
export function escalarCapa(c: CapaEnLienzo, factor: number): CapaEnLienzo {
  const minimo = LADO_MINIMO / Math.max(c.w, c.h);
  const f = Math.max(minimo, factor);
  return { ...c, w: c.w * f, h: c.h * f };
}

/** A cuántos grados de un ángulo recto se pega la rotación. */
export const IMAN_DE_ROTACION = 3;

/** La rotación, normalizada a (−180, 180] y pegada a 0/90/180/−90 cuando está cerca. */
export function rotacionConIman(grados: number): number {
  let g = ((grados % 360) + 360) % 360;
  if (g > 180) g -= 360;
  for (const recto of [-180, -90, 0, 90, 180]) {
    if (Math.abs(g - recto) <= IMAN_DE_ROTACION) return recto === -180 ? 180 : recto;
  }
  return Math.round(g * 10) / 10;
}

/** Mueve la capa `desde` a la posición `hasta` (índices del arreglo, de atrás hacia
 *  adelante). La base no se mueve ni nadie pasa detrás de ella. */
export function reordenarCapas(capas: readonly CapaEnLienzo[], desde: number, hasta: number): CapaEnLienzo[] {
  if (desde === hasta || capas[desde]?.esBase) return [...capas];
  const primeraMovible = capas[0]?.esBase ? 1 : 0;
  const destino = Math.min(capas.length - 1, Math.max(primeraMovible, hasta));
  const copia = [...capas];
  const [movida] = copia.splice(desde, 1);
  copia.splice(destino, 0, movida);
  return copia;
}

/** ¿Quedó todo como salió del separador? (sin cambios, guardar daría la misma imagen). */
export function sinCambios(actual: readonly CapaEnLienzo[], original: readonly CapaEnLienzo[]): boolean {
  if (actual.length !== original.length) return false;
  return actual.every((c, i) => {
    const o = original[i];
    return (
      c.id === o.id &&
      c.src === o.src &&
      c.visible === o.visible &&
      Math.abs(c.cx - o.cx) < 1e-4 &&
      Math.abs(c.cy - o.cy) < 1e-4 &&
      Math.abs(c.w - o.w) < 1e-4 &&
      Math.abs(c.h - o.h) < 1e-4 &&
      Math.abs(c.rot - o.rot) < 1e-3
    );
  });
}

// ── Editar una capa ─────────────────────────────────────────────────────────────
// ADR-2026-10-07-editar-una-capa-con-sunburst: Sunburst en `medium`, fondo transparente,
// la instrucción tal cual. 1 crédito.

/** Lo que cuesta editar una capa. Espejo de `CREDITOS_EDITAR_CAPA` del backend. */
export const CREDITOS_EDITAR_CAPA = 1;

export interface CapaEditadaDelBackend {
  path: string;
  /** Medidas de la capa que se mandó y del lienzo al que se rellenó (iguales si no hizo falta). */
  fuente: { ancho: number; alto: number };
  relleno: { ancho: number; alto: number };
}

/**
 * La capa con su PNG nuevo, a la MISMA escala: si el backend la rellenó con transparente
 * para entrar en 1:3–3:1, la caja crece lo mismo que creció el lienzo (el elemento queda
 * del mismo tamaño y en el mismo lugar). El centro y el giro no cambian.
 */
export function capaEditada(c: CapaEnLienzo, r: CapaEditadaDelBackend & { src: string }): CapaEnLienzo {
  const escalaX = r.fuente.ancho > 0 ? r.relleno.ancho / r.fuente.ancho : 1;
  const escalaY = r.fuente.alto > 0 ? r.relleno.alto / r.fuente.alto : 1;
  return { ...c, src: r.src, path: r.path, w: c.w * escalaX, h: c.h * escalaY };
}

/**
 * Un paso del historial de una capa editada (ADR-2026-10-07-revertir-la-edicion-de-una-capa):
 * el PNG de antes con su caja, y la caja con la que llegó la editada. Con las dos, revertir
 * respeta lo que el usuario hizo DESPUÉS de editar (moverla, escalarla, girarla).
 */
export interface EdicionDeCapa {
  src: string;
  path: string;
  w: number;
  h: number;
  wEditada: number;
  hEditada: number;
}

export function pasoDeHistorial(antes: CapaEnLienzo, editada: CapaEnLienzo): EdicionDeCapa {
  return { src: antes.src, path: antes.path, w: antes.w, h: antes.h, wEditada: editada.w, hEditada: editada.h };
}

/** Vuelve al PNG anterior: mismo centro y giro de ahora, y la escala que se le haya dado después. */
export function revertirEdicion(actual: CapaEnLienzo, paso: EdicionDeCapa): CapaEnLienzo {
  const escalaX = paso.wEditada > 0 ? actual.w / paso.wEditada : 1;
  const escalaY = paso.hEditada > 0 ? actual.h / paso.hEditada : 1;
  return { ...actual, src: paso.src, path: paso.path, w: paso.w * escalaX, h: paso.h * escalaY };
}

const MENSAJE_SIN_CREDITOS_CAPA =
  'No te alcanzan los créditos para editar la capa. Sumá créditos o actualizá tu plan para continuar.';

/** Edita una capa y devuelve la capa nueva. Tira `GenerateAdError` de billing en 402. */
export async function editarCapa(p: { capa: CapaEnLienzo; companyId: string; instruccion: string }): Promise<CapaEnLienzo> {
  const prefijo = `${p.companyId}/`;
  if (!p.capa.path.startsWith(prefijo)) throw new Error('No pudimos identificar esa capa. Probá separar de nuevo.');
  const res = await atlasBackendFetch('/v1/images/layers/editar-capa', {
    capa_key: p.capa.path.slice(prefijo.length),
    instruccion: p.instruccion.trim(),
    modo_utilizado: 'editar_capa_visor',
  });
  if (res.status === 402) throw new GenerateAdError('billing', MENSAJE_SIN_CREDITOS_CAPA, 'billing');
  const data = (await res.json().catch(() => null)) as (Partial<CapaEditadaDelBackend> & { response?: string; error?: string }) | null;
  if (data?.response === 'error_moderation') throw new Error('Ese cambio no pasó el filtro de contenido. Probá con otra instrucción.');
  if (!res.ok || data?.response !== 'success' || !data.path || !data.fuente || !data.relleno) {
    throw new Error(data?.error || 'No pudimos editar la capa. Probá de nuevo.');
  }
  const { data: firmada } = await supabase.storage.from('generadas').createSignedUrl(data.path, 3600);
  if (!firmada?.signedUrl) throw new Error('La capa se editó, pero no pudimos cargarla. Probá de nuevo.');
  return capaEditada(p.capa, { path: data.path, fuente: data.fuente, relleno: data.relleno, src: firmada.signedUrl });
}

// ── Componer y guardar ──────────────────────────────────────────────────────────

function cargarParaCanvas(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Firmadas de Supabase: sin esto el canvas queda "manchado" y no se puede exportar.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No pudimos leer una de las capas para guardar.'));
    img.src = src;
  });
}

/** La imagen final, del tamaño de la base: lo que el usuario ve en el lienzo. */
export async function componerCapas(capas: readonly CapaEnLienzo[], ancho: number, alto: number): Promise<Blob> {
  const visibles = capas.filter((c) => c.visible);
  const imagenes = await Promise.all(visibles.map((c) => cargarParaCanvas(c.src)));
  const canvas = document.createElement('canvas');
  canvas.width = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Tu navegador no pudo armar la imagen.');
  ctx.imageSmoothingQuality = 'high';
  visibles.forEach((c, i) => {
    ctx.save();
    ctx.translate(c.cx * ancho, c.cy * alto);
    ctx.rotate((c.rot * Math.PI) / 180);
    ctx.drawImage(imagenes[i], (-c.w * ancho) / 2, (-c.h * alto) / 2, c.w * ancho, c.h * alto);
    ctx.restore();
  });
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No pudimos armar la imagen.'))), 'image/png'),
  );
}

/** Sube la composición y la guarda como versión nueva. Devuelve el id de la fila. */
export async function guardarCapas(p: { imagenId: string; companyId: string; png: Blob; formato?: string }): Promise<string> {
  const key = `capas-composicion/${crypto.randomUUID()}.png`;
  const { error } = await supabase.storage
    .from('generadas')
    .upload(`${p.companyId}/${key}`, p.png, { contentType: 'image/png', upsert: false });
  if (error) throw new Error('No pudimos subir la imagen para guardarla. Probá de nuevo.');
  const res = await atlasBackendFetch('/v1/images/layers/guardar', {
    imagen_id: p.imagenId,
    source_key: key,
    ...(p.formato ? { formato: p.formato } : {}),
    modo_utilizado: 'separar_capas_visor',
  });
  const data = (await res.json().catch(() => null)) as { response?: string; edited_image_id?: string | number; error?: string } | null;
  if (!res.ok || data?.response !== 'success' || !data.edited_image_id) {
    throw new Error(data?.error || 'No pudimos guardar la versión. Probá de nuevo.');
  }
  return String(data.edited_image_id);
}
